import { useCallback } from 'react';
import {
  FieldPath,
  FieldPathValue,
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { Radio, RadioGroupProps } from 'antd';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import { useAdditionalFormContext } from './AdditionalFormContext';
import { useGetIdFromName } from './utils';

export interface FormRadioGroupProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FieldPathValue<FormData, Name> = FieldPathValue<FormData, Name>
> extends Omit<RadioGroupProps, 'name' | 'value'> {
  name: Name;
  id?: string;
  options: Array<{
    value: Value;
    label: string;
  }>;
  disableValueChange?: boolean;
  type?: 'radio' | 'button';
}

export const FormRadioGroup = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FieldPathValue<FormData, Name> = FieldPathValue<FormData, Name>
>(
  props: FormRadioGroupProps<FormData, Name, Value>
) => {
  const getIdFromName = useGetIdFromName();
  const {
    name,
    id = getIdFromName(name),
    options,
    disableValueChange,
    onChange,
    type = 'radio',
    ...rest
  } = props;
  const form = useFormContext<FormData>();
  const value = useWatch({ name });
  const { onChange: formOnChange } = useAdditionalFormContext();

  const handleChange = useCallback(
    (event: RadioChangeEvent) => {
      if (!disableValueChange) {
        form.setValue(name, event.target.value, { shouldDirty: true });
        formOnChange?.();
      }
      onChange?.(event);
    },
    [disableValueChange, form, name, onChange]
  );

  return (
    <Radio.Group id={id} value={value} onChange={handleChange} {...rest}>
      {options.map((option) => {
        const RadioComponent = type === 'button' ? Radio.Button : Radio;
        return (
          <RadioComponent
            id={`${id}-${option.label.toLowerCase().replace(/[.\s]/g, '-')}`}
            key={option.value}
            value={option.value}
          >
            {option.label}
          </RadioComponent>
        );
      })}
    </Radio.Group>
  );
};
